/**
 * New Year game popups — one SweetAlert design for every player-facing dialog:
 * answer feedback, end-of-stage results, confirmations and notices.
 * Buttons and accents follow the admin Button colors (--ny-accent*, see ny.css).
 */
import Swal from "sweetalert2";
import "./ny.css";

// How long the timed right / wrong popups stay up (wrong also shows the right answer).
const CORRECT_MS = 2400;
const WRONG_MS = 3600;

function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Full customClass set (SweetAlert replaces, not merges, customClass per call). */
function popupClasses(variant = "") {
  return {
    container: "ny-swal-backdrop",
    popup: `ny-swal${variant ? ` ny-swal--${variant}` : ""}`,
    title: "ny-swal-title",
    htmlContainer: "ny-swal-text",
    actions: "ny-swal-actions",
    confirmButton: "ny-btn ny-btn--primary",
    cancelButton: "ny-btn ny-btn--light",
    timerProgressBar: "ny-swal-timer",
  };
}

const base = {
  buttonsStyling: false,
  showClass: { popup: "ny-swal-in" },
  hideClass: { popup: "ny-swal-out" },
};

const BADGE_ICON = {
  correct: "fa-check",
  wrong: "fa-xmark",
  info: "fa-info",
  question: "fa-question",
  warning: "fa-exclamation",
  error: "fa-xmark",
  star: "fa-star",
};

function badge(kind) {
  return `<div class="ny-pop-badge ny-pop-badge--${kind}" aria-hidden="true">
    <span class="ny-pop-burst"></span>
    <i class="fa-solid ${BADGE_ICON[kind] || BADGE_ICON.info}"></i>
  </div>`;
}

/** Timed right / wrong popup after answering a question. */
export function showAnswerFeedback({ correct, points = 0, answer = "" }) {
  const html = correct
    ? `<div class="ny-pop">${badge("correct")}
        <h3 class="ny-pop-title">Correct!</h3>
        <p class="ny-pop-text">Great job, keep it up.</p>
        <span class="ny-pop-chip"><i class="fa-solid fa-star"></i> +${escapeHtml(points)} points</span>
      </div>`
    : `<div class="ny-pop">${badge("wrong")}
        <h3 class="ny-pop-title">Not quite!</h3>
        <p class="ny-pop-text">The correct answer is</p>
        <span class="ny-pop-answer">${escapeHtml(answer)}</span>
      </div>`;
  return Swal.fire({
    ...base,
    html,
    timer: correct ? CORRECT_MS : WRONG_MS,
    timerProgressBar: true,
    showConfirmButton: false,
    customClass: popupClasses(correct ? "correct" : "wrong"),
  });
}

/** End-of-stage summary with a Continue button. */
export function showStageResult({ correct, total, points, confirmText = "Continue" }) {
  const perfect = total > 0 && correct === total;
  const html = `<div class="ny-pop">${badge("star")}
    <h3 class="ny-pop-title">${perfect ? "Perfect!" : "Stage complete!"}</h3>
    <p class="ny-pop-text">You got <strong>${escapeHtml(correct)}</strong> of <strong>${escapeHtml(total)}</strong> right</p>
    <span class="ny-pop-chip"><i class="fa-solid fa-star"></i> +${escapeHtml(points)} points</span>
  </div>`;
  return Swal.fire({
    ...base,
    html,
    confirmButtonText: `${escapeHtml(confirmText)} <i class="fa-solid fa-arrow-right"></i>`,
    allowOutsideClick: false,
    customClass: popupClasses("result"),
  });
}

/** Notice with one button. kind: info | warning | error | correct | star */
export function nyAlert({ kind = "info", title = "", text = "", confirmText = "OK", ...rest }) {
  return Swal.fire({
    ...base,
    html: `<div class="ny-pop">${badge(kind)}
      ${title ? `<h3 class="ny-pop-title">${escapeHtml(title)}</h3>` : ""}
      ${text ? `<p class="ny-pop-text">${escapeHtml(text)}</p>` : ""}
    </div>`,
    confirmButtonText: escapeHtml(confirmText),
    customClass: popupClasses(kind),
    ...rest,
  });
}

/**
 * "How was your experience?" — emoji choices, one tap to answer.
 * Resolves to the chosen option's `value`. The popup can't be dismissed
 * without choosing (it's shown before the score screen).
 */
export function askFeedback({ options, title = "How was your experience?", text = "Tap an emoji to share your thoughts" }) {
  let chosen = null;
  const chips = options
    .map(
      (opt) => `<button type="button" class="ny-pop-emoji" data-value="${escapeHtml(opt.value)}">
        <span class="ny-pop-emoji-icon" aria-hidden="true">${opt.emoji}</span>
        <span class="ny-pop-emoji-label">${escapeHtml(opt.label)}</span>
      </button>`
    )
    .join("");

  return Swal.fire({
    ...base,
    html: `<div class="ny-pop">${badge("star")}
      <h3 class="ny-pop-title">${escapeHtml(title)}</h3>
      <p class="ny-pop-text">${escapeHtml(text)}</p>
      <div class="ny-pop-emoji-grid">${chips}</div>
    </div>`,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    customClass: popupClasses("feedback"),
    didOpen: (popup) => {
      popup.querySelectorAll(".ny-pop-emoji").forEach((btn) => {
        btn.addEventListener("click", () => {
          chosen = btn.getAttribute("data-value");
          btn.classList.add("is-selected");
          // Brief beat so the tap registers visually before closing.
          window.setTimeout(() => Swal.close(), 260);
        });
      });
    },
  }).then(() => chosen);
}

/** Yes / no question. Resolves to SweetAlert's result ({ isConfirmed }). */
export function nyConfirm({ title = "", text = "", confirmText = "Yes", cancelText = "Cancel", kind = "question" }) {
  return Swal.fire({
    ...base,
    html: `<div class="ny-pop">${badge(kind)}
      ${title ? `<h3 class="ny-pop-title">${escapeHtml(title)}</h3>` : ""}
      ${text ? `<p class="ny-pop-text">${escapeHtml(text)}</p>` : ""}
    </div>`,
    showCancelButton: true,
    confirmButtonText: escapeHtml(confirmText),
    cancelButtonText: escapeHtml(cancelText),
    reverseButtons: true,
    customClass: popupClasses(kind),
  });
}
